import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { 
  Shield, 
  ShieldCheck, 
  ShieldOff, 
  Key, 
  RefreshCw, 
  Copy, 
  Download,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Eye,
  EyeOff
} from 'lucide-react';
import { cn } from '../lib/utils';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';



export function TwoFactorSettings() {
  const { getAuthHeaders } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enabling, setEnabling] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  
  // Form states
  const [showEnableForm, setShowEnableForm] = useState(false);
  const [showDisableForm, setShowDisableForm] = useState(false);
  const [showRecoveryCodes, setShowRecoveryCodes] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [useRecoveryForDisable, setUseRecoveryForDisable] = useState(false);
  
  // Fetch 2FA status
  const fetchStatus = async () => {
    try {
      const response = await fetch(`${API}/2fa/status`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (error) {
      console.error('Error fetching 2FA status:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchStatus();
  }, []);
  
  // Enable 2FA
  const handleEnable = async () => {
    if (!password) {
      toast.error('Please enter your password');
      return;
    }
    
    setEnabling(true);
    try {
      const response = await fetch(`${API}/2fa/enable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ password })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Two-factor authentication enabled!');
        setRecoveryCodes(data.recovery_codes);
        setShowRecoveryCodes(true);
        setShowEnableForm(false);
        setPassword('');
        fetchStatus();
      } else {
        toast.error(data.detail || 'Failed to enable 2FA');
      }
    } catch (error) {
      toast.error('Failed to enable 2FA');
    } finally {
      setEnabling(false);
    }
  };
  
  // Send disable OTP code
  const sendDisableCode = async () => {
    try {
      const response = await fetch(`${API}/2fa/send-disable-code`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Verification code sent to your email');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to send code');
      }
    } catch (error) {
      toast.error('Failed to send verification code');
    }
  };
  
  // Disable 2FA
  const handleDisable = async () => {
    if (useRecoveryForDisable) {
      if (!recoveryCode) {
        toast.error('Please enter a recovery code');
        return;
      }
    } else {
      if (!password || !otpCode) {
        toast.error('Please enter your password and verification code');
        return;
      }
    }
    
    setDisabling(true);
    try {
      const body = useRecoveryForDisable
        ? { recovery_code: recoveryCode }
        : { password, otp_code: otpCode };
      
      const response = await fetch(`${API}/2fa/disable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(body)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Two-factor authentication disabled');
        setShowDisableForm(false);
        setPassword('');
        setOtpCode('');
        setRecoveryCode('');
        fetchStatus();
      } else {
        toast.error(data.detail || 'Failed to disable 2FA');
      }
    } catch (error) {
      toast.error('Failed to disable 2FA');
    } finally {
      setDisabling(false);
    }
  };
  
  // Regenerate recovery codes
  const handleRegenerateCodes = async () => {
    if (!password) {
      toast.error('Please enter your password');
      return;
    }
    
    setRegenerating(true);
    try {
      const response = await fetch(`${API}/2fa/regenerate-recovery-codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ password })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Recovery codes regenerated!');
        setRecoveryCodes(data.recovery_codes);
        setShowRecoveryCodes(true);
        setPassword('');
        fetchStatus();
      } else {
        toast.error(data.detail || 'Failed to regenerate codes');
      }
    } catch (error) {
      toast.error('Failed to regenerate recovery codes');
    } finally {
      setRegenerating(false);
    }
  };
  
  // Copy codes to clipboard
  const copyCodestoClipboard = () => {
    const codesText = recoveryCodes.join('\n');
    try { navigator.clipboard.writeText(codesText).catch(() => {}); } catch {}
    toast.success('Recovery codes copied to clipboard');
  };
  
  // Download codes as file
  const downloadCodes = () => {
    const codesText = `DMV Soundstage Recovery Codes\n${'='.repeat(40)}\n\nSave these codes in a secure location.\nEach code can only be used once.\n\n${recoveryCodes.join('\n')}\n\nGenerated: ${new Date().toLocaleString()}`;
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'dmv-soundstage-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Recovery codes downloaded');
  };
  
  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </Card>
    );
  }
  
  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Shield className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h3 className="font-heading text-lg font-bold">Two-Factor Authentication</h3>
          <p className="text-sm text-muted-foreground">Add an extra layer of security to your account</p>
        </div>
      </div>
      
      {/* Status Display */}
      <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg mb-6">
        <div className="flex items-center gap-3">
          {status?.two_factor_enabled ? (
            <>
              <ShieldCheck className="w-5 h-5 text-green-500" />
              <div>
                <p className="font-medium text-green-500">2FA Enabled</p>
                <p className="text-xs text-muted-foreground">
                  Method: Email OTP • {status.recovery_codes_count} recovery codes remaining
                </p>
              </div>
            </>
          ) : (
            <>
              <ShieldOff className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="font-medium">2FA Disabled</p>
                <p className="text-xs text-muted-foreground">Your account uses password-only authentication</p>
              </div>
            </>
          )}
        </div>
        <Badge variant={status?.two_factor_enabled ? "success" : "secondary"}>
          {status?.two_factor_enabled ? "Active" : "Inactive"}
        </Badge>
      </div>
      
      {/* Recovery Codes Modal */}
      {showRecoveryCodes && recoveryCodes.length > 0 && (
        <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
            <div>
              <p className="font-medium text-amber-500">Save Your Recovery Codes</p>
              <p className="text-sm text-muted-foreground">
                Store these codes in a safe place. Each code can only be used once.
                You will not be able to see these codes again.
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-2 p-4 bg-black/20 rounded-lg font-mono text-sm mb-4">
            {recoveryCodes.map((code, index) => (
              <div key={index} className="p-2 bg-muted/30 rounded">
                {code}
              </div>
            ))}
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={copyCodestoClipboard}>
              <Copy className="w-4 h-4 mr-2" /> Copy
            </Button>
            <Button variant="outline" size="sm" onClick={downloadCodes}>
              <Download className="w-4 h-4 mr-2" /> Download
            </Button>
            <Button 
              size="sm" 
              onClick={() => {
                setShowRecoveryCodes(false);
                setRecoveryCodes([]);
              }}
              className="ml-auto"
            >
              <CheckCircle className="w-4 h-4 mr-2" /> I&apos;ve Saved These Codes
            </Button>
          </div>
        </div>
      )}
      
      {/* Enable 2FA Section */}
      {!status?.two_factor_enabled && !showEnableForm && (
        <Button onClick={() => setShowEnableForm(true)} className="w-full">
          <ShieldCheck className="w-4 h-4 mr-2" /> Enable Two-Factor Authentication
        </Button>
      )}
      
      {!status?.two_factor_enabled && showEnableForm && (
        <div className="space-y-4 p-4 border border-border rounded-lg">
          <h4 className="font-medium">Enable 2FA</h4>
          <p className="text-sm text-muted-foreground">
            A verification code will be sent to your email each time you log in.
          </p>
          
          <div className="space-y-2">
            <Label>Confirm Your Password</Label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowEnableForm(false);
                setPassword('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleEnable} disabled={enabling}>
              {enabling ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enabling...</>
              ) : (
                <><ShieldCheck className="w-4 h-4 mr-2" /> Enable 2FA</>
              )}
            </Button>
          </div>
        </div>
      )}
      
      {/* Disable 2FA Section */}
      {status?.two_factor_enabled && !showDisableForm && (
        <div className="space-y-3">
          <Button variant="outline" onClick={() => setShowDisableForm(true)} className="w-full">
            <ShieldOff className="w-4 h-4 mr-2" /> Disable Two-Factor Authentication
          </Button>
          
          <Button 
            variant="ghost" 
            onClick={() => {
              setShowRecoveryCodes(false);
              setPassword('');
            }}
            className="w-full"
          >
            <Key className="w-4 h-4 mr-2" /> View / Regenerate Recovery Codes
          </Button>
        </div>
      )}
      
      {status?.two_factor_enabled && showDisableForm && (
        <div className="space-y-4 p-4 border border-red-500/30 rounded-lg">
          <h4 className="font-medium text-red-400">Disable 2FA</h4>
          <p className="text-sm text-muted-foreground">
            This will remove the extra security layer from your account.
          </p>
          
          <div className="flex items-center gap-2">
            <Switch
              checked={useRecoveryForDisable}
              onCheckedChange={setUseRecoveryForDisable}
            />
            <Label className="text-sm">Use recovery code instead</Label>
          </div>
          
          {useRecoveryForDisable ? (
            <div className="space-y-2">
              <Label>Recovery Code</Label>
              <Input
                type="text"
                value={recoveryCode}
                onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                placeholder="XXXX-XXXX"
                className="font-mono"
              />
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label>Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Verification Code</Label>
                  <Button variant="link" size="sm" onClick={sendDisableCode} className="h-auto p-0">
                    Send Code
                  </Button>
                </div>
                <Input
                  type="text"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  className="font-mono tracking-widest text-center text-lg"
                />
              </div>
            </>
          )}
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDisableForm(false);
                setPassword('');
                setOtpCode('');
                setRecoveryCode('');
              }}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDisable} disabled={disabling}>
              {disabling ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Disabling...</>
              ) : (
                <><ShieldOff className="w-4 h-4 mr-2" /> Disable 2FA</>
              )}
            </Button>
          </div>
        </div>
      )}
      
      {/* Regenerate Recovery Codes (when 2FA is enabled) */}
      {status?.two_factor_enabled && !showDisableForm && !showRecoveryCodes && (
        <div className="mt-4 p-4 border border-border rounded-lg">
          <h4 className="font-medium mb-2">Regenerate Recovery Codes</h4>
          <p className="text-sm text-muted-foreground mb-4">
            If you&apos;ve lost your recovery codes or used most of them, you can generate new ones.
            This will invalidate all existing recovery codes.
          </p>
          
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Confirm Your Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
              />
            </div>
            
            <Button onClick={handleRegenerateCodes} disabled={regenerating} variant="outline">
              {regenerating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Regenerating...</>
              ) : (
                <><RefreshCw className="w-4 h-4 mr-2" /> Regenerate Codes</>
              )}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

export default TwoFactorSettings;
